"use client";

/**
 * HomePage — ERP Dashboard (1:1 mockup port).
 *
 * Mockup: docs/mockups/erp-skeleton-v1-popunjen.html (lin 1171-1335).
 * Layout (mockup main + rail):
 *
 *  ┌──────────────────────────────────────┐ ┌──────────────┐
 *  │ Tabs (Pregled / Statistika / ...)    │ │ Aktivnost    │
 *  │ Page header (Dobrodošli + CTA)       │ │ Tim online   │
 *  │ KPI grid (4 kartice)                 │ │ Brze akcije  │
 *  │ Card (Posljednje fakture, empty)     │ │ Najavljeno   │
 *  └──────────────────────────────────────┘ └──────────────┘
 *
 * Mock podaci dok backend modula ne agregira KPI / activity.
 *
 * Pravila:
 *  - PRAVILO 12: CSS vars only, dark mode, Manrope/Inter
 *  - PRAVILO 13: ne dira sveta zona u topbar-u
 *  - Crnogorski latinica
 */

import * as React from "react";
import {
  FileText,
  Receipt,
  AlertCircle,
  CreditCard,
  TrendingUp,
  Plus,
  Download,
  Users,
  Package,
  Upload,
  Bell,
} from "@grozd/icons";
import type { LucideIcon } from "@grozd/icons";

interface KpiCard {
  label: string;
  value: string;
  currency?: string;
  delta: { text: string; tone: "up" | "down" | "neutral" };
  icon: LucideIcon;
  iconTone?: "primary" | "danger" | "info" | "success";
}

const KPIS: KpiCard[] = [
  {
    label: "Promet (maj)",
    value: "18.420",
    currency: "€",
    delta: { text: "12.4% vs april", tone: "up" },
    icon: TrendingUp,
    iconTone: "primary",
  },
  {
    label: "Otvorene fakture",
    value: "7",
    delta: { text: "↓ 2 vs prošla nedelja", tone: "down" },
    icon: FileText,
    iconTone: "primary",
  },
  {
    label: "Naplata u kašnjenju",
    value: "2.840",
    currency: "€",
    delta: { text: "3 dospjele opomene", tone: "down" },
    icon: AlertCircle,
    iconTone: "danger",
  },
  {
    label: "Stanje banke",
    value: "12.105",
    currency: "€",
    delta: { text: "Live · usklađeno", tone: "up" },
    icon: CreditCard,
    iconTone: "info",
  },
];

const ACTIVITIES = [
  {
    initials: "MP",
    bg: "linear-gradient(135deg,#16A34A,#0E7C38)",
    text: (
      <>
        <strong>Marko Petrović</strong> kreirao fakturu{" "}
        <strong>#2026-0042</strong>
      </>
    ),
    meta: "prije 12 minuta",
  },
  {
    initials: "VM",
    bg: "linear-gradient(135deg, var(--primary), var(--deep))",
    text: (
      <>
        Faktura <strong>#2026-0041 plaćena</strong> · €1.240
      </>
    ),
    meta: "prije 1h 12min",
  },
  {
    initials: "AS",
    bg: "linear-gradient(135deg,#D97706,#92400E)",
    text: (
      <>
        <strong>Ana Stojanović</strong> odobrila ponudu <strong>#P-058</strong>
      </>
    ),
    meta: "prije 3h",
  },
];

export default function HomePage() {
  const [activeTab, setActiveTab] = React.useState<"pregled" | "statistika" | "istorija">(
    "pregled"
  );

  const today = React.useMemo(() => {
    return new Date().toLocaleDateString("sr-Latn-ME", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }, []);

  const userFirstName = "Velibore"; // placeholder dok ne integrišemo user kontekst

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1fr 320px",
        minHeight: "100%",
      }}
      className="erp-dashboard"
    >
      {/* MAIN */}
      <main
        style={{
          padding: "20px 24px 28px",
          minWidth: 0,
        }}
      >
        {/* Tabs */}
        <div
          className="flex gap-0.5"
          style={{ borderBottom: "1px solid var(--n100)", marginBottom: 18 }}
          role="tablist"
        >
          {(["pregled", "statistika", "istorija"] as const).map((tab) => {
            const labels = {
              pregled: "Pregled",
              statistika: "Statistika",
              istorija: "Istorija promjena",
            };
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => setActiveTab(tab)}
                className="font-body"
                style={{
                  padding: "10px 14px",
                  fontSize: 13,
                  fontWeight: 600,
                  color: active ? "var(--primary)" : "var(--n400)",
                  cursor: "pointer",
                  borderBottom: `2px solid ${active ? "var(--primary)" : "transparent"}`,
                  marginBottom: -1,
                  background: "transparent",
                  border: "none",
                  borderBottomWidth: 2,
                  borderBottomStyle: "solid",
                  transition: "all 120ms",
                }}
              >
                {labels[tab]}
              </button>
            );
          })}
        </div>

        {/* Page header */}
        <div
          style={{
            marginBottom: 18,
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 16,
            flexWrap: "wrap",
          }}
        >
          <div>
            <h1
              className="font-brand"
              style={{
                fontWeight: 800,
                fontSize: 24,
                letterSpacing: "-0.025em",
                color: "var(--ink)",
              }}
            >
              Dobrodošli, {userFirstName}
            </h1>
            <p
              className="font-body"
              style={{ fontSize: 13, color: "var(--n400)", marginTop: 6 }}
            >
              DOT PRO d.o.o. — pregled poslovanja, {today}.
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button type="button" style={btnGhost}>
              <Download size={14} strokeWidth={2} />
              Izvezi
            </button>
            <button type="button" style={btnPrimary}>
              <Plus size={14} strokeWidth={2} />
              Nova faktura
            </button>
          </div>
        </div>

        {/* KPI grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(4, 1fr)",
            gap: 12,
            marginBottom: 20,
          }}
          className="erp-kpi-grid"
        >
          {KPIS.map((kpi) => (
            <KpiTile key={kpi.label} kpi={kpi} />
          ))}
        </div>

        {/* Card sa empty state */}
        <div
          style={{
            background: "var(--surface-card)",
            border: "1px solid var(--n100)",
            borderRadius: 14,
            overflow: "hidden",
          }}
        >
          <div
            style={{
              padding: "14px 18px",
              borderBottom: "1px solid var(--n100)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
            }}
          >
            <div className="flex items-center gap-2">
              <span
                className="font-brand"
                style={{
                  fontWeight: 700,
                  fontSize: 14.5,
                  color: "var(--ink)",
                }}
              >
                Posljednje fakture
              </span>
              <span
                className="font-brand"
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "2px 7px",
                  borderRadius: 999,
                  background: "var(--primary)",
                  color: "#fff",
                }}
              >
                0
              </span>
            </div>
            <div className="flex items-center gap-1.5">
              <button type="button" style={{ ...btnGhost, height: 30, padding: "0 10px", fontSize: 12 }}>
                Filteri
              </button>
              <button type="button" style={{ ...btnGhost, height: 30, padding: "0 10px", fontSize: 12 }}>
                Pogledaj sve →
              </button>
            </div>
          </div>
          <div style={{ padding: "48px 18px", textAlign: "center" }}>
            <div
              style={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                background: "var(--p100)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "var(--primary)",
                marginBottom: 14,
              }}
            >
              <FileText size={28} strokeWidth={1.8} />
            </div>
            <div
              className="font-brand"
              style={{
                fontWeight: 700,
                fontSize: 14,
                color: "var(--ink)",
                marginBottom: 6,
              }}
            >
              Nema faktura u zadnjih 7 dana
            </div>
            <div
              className="font-body"
              style={{
                fontSize: 13,
                color: "var(--n400)",
                maxWidth: 320,
                margin: "0 auto",
              }}
            >
              Klikni{" "}
              <strong style={{ color: "var(--primary)" }}>Nova faktura</strong>{" "}
              ili pritisni{" "}
              <span
                className="font-mono"
                style={{
                  display: "inline-flex",
                  padding: "2px 6px",
                  borderRadius: 4,
                  background: "var(--n50)",
                  border: "1px solid var(--n100)",
                  fontSize: 10.5,
                  color: "var(--n600)",
                  fontWeight: 600,
                }}
              >
                N
              </span>{" "}
              da kreneš sa fakturisanjem.
            </div>
          </div>
        </div>
      </main>

      {/* RIGHT RAIL */}
      <aside
        className="erp-rail"
        style={{
          background: "var(--surface-card)",
          borderLeft: "1px solid var(--n100)",
          padding: "18px 16px",
          overflowY: "auto",
        }}
      >
        {/* Aktivnost danas */}
        <div style={{ marginBottom: 20 }}>
          <div
            className="flex items-center justify-between"
            style={{ marginBottom: 12 }}
          >
            <div
              className="font-brand"
              style={{
                fontWeight: 700,
                fontSize: 11,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "var(--n400)",
              }}
            >
              Aktivnost danas
            </div>
            <span
              className="font-body"
              style={{
                fontSize: 11,
                color: "var(--n400)",
                cursor: "pointer",
                fontWeight: 600,
              }}
            >
              Sve →
            </span>
          </div>
          {ACTIVITIES.map((a, i) => (
            <div
              key={i}
              className="flex gap-2.5"
              style={{
                marginBottom: 12,
                fontSize: 12.5,
                padding: 6,
                borderRadius: 10,
              }}
            >
              <div
                className="font-brand"
                style={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  flexShrink: 0,
                  background: a.bg,
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: 11,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {a.initials}
              </div>
              <div>
                <div
                  className="font-body"
                  style={{ color: "var(--n600)", lineHeight: 1.45 }}
                >
                  {a.text}
                </div>
                <div
                  className="font-mono"
                  style={{
                    color: "var(--n400)",
                    fontSize: 11,
                    marginTop: 3,
                  }}
                >
                  {a.meta}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Brze akcije */}
        <div style={{ marginBottom: 20 }}>
          <div
            className="font-brand"
            style={{
              fontWeight: 700,
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--n400)",
              marginBottom: 12,
            }}
          >
            Brze akcije
          </div>
          <div
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--n100)",
              borderRadius: 14,
              padding: 4,
            }}
          >
            <QuickAction icon={Upload} label="Uvezi izvod banke" kbd="U" />
            <QuickAction icon={Users} label="Dodaj kupca" kbd="K" />
            <QuickAction icon={Package} label="Nova roba" kbd="R" />
            <QuickAction icon={Receipt} label="Nova ponuda" kbd="P" />
          </div>
        </div>

        {/* Najavljeno */}
        <div>
          <div
            className="font-brand"
            style={{
              fontWeight: 700,
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.08em",
              color: "var(--n400)",
              marginBottom: 12,
            }}
          >
            Najavljeno
          </div>
          <div
            style={{
              background: "var(--surface-card)",
              border: "1px solid var(--n100)",
              borderRadius: 14,
              padding: "14px 18px",
              fontSize: 12.5,
            }}
          >
            <span
              className="font-brand"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                padding: "3px 9px",
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 600,
                background: "var(--warning-light)",
                color: "var(--warning)",
              }}
            >
              <Bell size={12} strokeWidth={2} />
              Sjutra 02:00 — 02:30
            </span>
            <div
              style={{
                marginTop: 8,
                color: "var(--n600)",
                lineHeight: 1.5,
              }}
            >
              <strong>Backup baze + bezbjednosna nadogradnja.</strong> ERP će
              biti privremeno nedostupan ~30 min.
            </div>
          </div>
        </div>
      </aside>

      {/* Container queries — rail hide na uskim ekranima */}
      <style jsx>{`
        @media (max-width: 1100px) {
          .erp-dashboard {
            grid-template-columns: 1fr !important;
          }
          .erp-rail {
            display: none !important;
          }
          .erp-kpi-grid {
            grid-template-columns: repeat(2, 1fr) !important;
          }
        }
        @media (max-width: 640px) {
          .erp-kpi-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </div>
  );
}

function KpiTile({ kpi }: { kpi: KpiCard }) {
  const Icon = kpi.icon;
  const iconBg = {
    primary: { bg: "var(--p100)", color: "var(--primary)" },
    danger: { bg: "var(--danger-light)", color: "var(--danger)" },
    info: { bg: "var(--info-light)", color: "var(--info)" },
    success: { bg: "var(--success-light)", color: "var(--success)" },
  }[kpi.iconTone ?? "primary"];

  const deltaColor = {
    up: "var(--success)",
    down: "var(--danger)",
    neutral: "var(--n400)",
  }[kpi.delta.tone];

  return (
    <div
      style={{
        background: "var(--surface-card)",
        border: "1px solid var(--n100)",
        borderRadius: 14,
        padding: 16,
        position: "relative",
        overflow: "hidden",
        transition: "all 120ms",
      }}
    >
      <div
        className="flex items-center justify-between"
        style={{ marginBottom: 10 }}
      >
        <span
          className="font-brand"
          style={{
            fontSize: 11.5,
            fontWeight: 600,
            color: "var(--n400)",
            textTransform: "uppercase",
            letterSpacing: "0.06em",
          }}
        >
          {kpi.label}
        </span>
        <span
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: iconBg.bg,
            color: iconBg.color,
          }}
        >
          <Icon size={14} strokeWidth={2} />
        </span>
      </div>
      <div
        className="font-brand"
        style={{
          fontWeight: 800,
          fontSize: 22,
          letterSpacing: "-0.02em",
          color: "var(--ink)",
          marginBottom: 6,
          display: "flex",
          alignItems: "baseline",
          gap: 6,
        }}
      >
        {kpi.currency && (
          <span style={{ fontSize: 14, fontWeight: 600, color: "var(--n400)" }}>
            {kpi.currency}
          </span>
        )}
        {kpi.value}
      </div>
      <div
        className="font-body"
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: deltaColor,
        }}
      >
        {kpi.delta.text}
      </div>
    </div>
  );
}

function QuickAction({
  icon: Icon,
  label,
  kbd,
}: {
  icon: LucideIcon;
  label: string;
  kbd: string;
}) {
  return (
    <button
      type="button"
      className="erp-qa"
      style={{
        width: "100%",
        height: 38,
        padding: "0 12px",
        margin: "4px 0",
        borderRadius: 10,
        display: "flex",
        alignItems: "center",
        gap: 10,
        fontSize: 13,
        fontWeight: 500,
        color: "var(--n600)",
        cursor: "pointer",
        background: "transparent",
        border: "none",
        textAlign: "left",
        transition: "all 120ms",
      }}
    >
      <Icon size={16} strokeWidth={1.8} />
      <span style={{ flex: 1 }}>{label}</span>
      <span
        className="font-mono"
        style={{
          fontSize: 10,
          color: "var(--n400)",
          padding: "2px 6px",
          borderRadius: 4,
          background: "var(--n50)",
          border: "1px solid var(--n100)",
          fontWeight: 600,
        }}
      >
        {kbd}
      </span>
    </button>
  );
}

const btnPrimary: React.CSSProperties = {
  height: 36,
  padding: "0 14px",
  border: "none",
  borderRadius: 10,
  cursor: "pointer",
  fontFamily: "var(--font-brand), system-ui, sans-serif",
  fontWeight: 600,
  fontSize: 13,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "var(--primary)",
  color: "#fff",
  transition: "all 120ms",
};

const btnGhost: React.CSSProperties = {
  height: 36,
  padding: "0 14px",
  border: "1px solid var(--n100)",
  borderRadius: 10,
  cursor: "pointer",
  fontFamily: "var(--font-brand), system-ui, sans-serif",
  fontWeight: 600,
  fontSize: 13,
  display: "inline-flex",
  alignItems: "center",
  gap: 6,
  background: "transparent",
  color: "var(--n600)",
  transition: "all 120ms",
};
